import { memo, useRef, useLayoutEffect, useMemo } from "react";
import _get from "lodash/get";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import Spin from "antd/lib/spin";
import Row from "antd/lib/row";
import MessageCell from "./MessageCell";

const MessageList = ({
  messageIds = [],
  messageEntities = {},
  getMessageList,
  hasMore,
  currentUserId,
  currentUserRole,
  optimisticMessages = [],
  onRetry,
  firstGetList,
}) => {
  const latestMessage = messageIds[0];
  const messageListRef = useRef();

  // Combine optimistic messages with real messages
  const allMessages = useMemo(() => {
    const optimisticIds = optimisticMessages.map((msg) => msg._id);
    return [...optimisticIds, ...messageIds];
  }, [messageIds, optimisticMessages]);

  const allMessageEntities = useMemo(() => {
    const entities = { ...messageEntities };
    optimisticMessages.forEach((msg) => {
      entities[msg._id] = msg;
    });
    return entities;
  }, [messageEntities, optimisticMessages]);

  useLayoutEffect(() => {
    messageListRef.current.scrollTo(0, 0);
  }, [latestMessage]);

  // Show loading on first load
  if (!firstGetList) {
    return (
      <div className="message-list-container" ref={messageListRef}>
        <Row style={{ padding: 40 }} justify="center">
          <Spin size="large" />
        </Row>
      </div>
    );
  }

  return (
    <div
      className="message-list-container"
      id="scrollable-message-list"
      ref={messageListRef}
    >
      <InfiniteScroll
        scrollThreshold="100px"
        dataLength={allMessages.length}
        next={getMessageList}
        hasMore={hasMore}
        loader={
          <Row style={{ padding: 8 }} justify="center">
            <Spin />
          </Row>
        }
        style={{ display: "flex", flexDirection: "column-reverse" }}
        inverse
        scrollableTarget="scrollable-message-list"
      >
        {allMessages.map((id) => {
          const msgData = allMessageEntities[id];
          const senderId =
            _get(msgData, "sender._id") || _get(msgData, "sender");
          return (
            <MessageCell
              isOwnCell={
                senderId === currentUserId ||
                (["admin", "super_admin"].includes(currentUserRole) &&
                  _get(msgData, "isAdmin"))
              }
              key={id}
              data={msgData}
              onRetry={onRetry}
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

MessageList.propTypes = {
  messageIds: PropTypes.array.isRequired,
  messageEntities: PropTypes.object.isRequired,
  getMessageList: PropTypes.func,
  hasMore: PropTypes.bool,
  currentUserId: PropTypes.string,
  currentUserRole: PropTypes.string,
  optimisticMessages: PropTypes.array,
  onRetry: PropTypes.func,
  firstGetList: PropTypes.bool,
};

export default memo(MessageList);
